import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Minus, Palette } from 'lucide-react';
import { SURFACE_BLEND_GROUPS } from '@/data/probound';

const StepSurfacing = ({
  surfacingItems,
  onPriceChange,
  onSquareMetresChange,
  onBlendChange,
  totals,
  hasItems,
  onBack,
  onContinue
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#27292E] mb-2">
        Surfacing Details
      </h2>
      <p className="text-gray-600 mb-6">
        Enter the square metres for each surfacing type. Adjust the price per m² if needed.
      </p>

      <div className="space-y-6">
        {surfacingItems.map((item) => (
          <div 
            key={item.id}
            className={`border-2 rounded-xl p-5 transition-all relative ${
              item.isPremium 
                ? item.squareMetres > 0 
                  ? 'border-[#2F5E96] bg-gradient-to-br from-[#2F5E96]/10 to-[#2F5E96]/5 shadow-lg' 
                  : 'border-[#2F5E96] bg-gradient-to-br from-[#2F5E96]/5 to-white shadow-md'
                : item.squareMetres > 0 
                  ? 'border-[#300122] bg-[#300122]/5' 
                  : 'border-gray-200'
            }`}
            data-testid={`surfacing-${item.id}`}
          >
            {/* Premium Badge */}
            {item.isPremium && item.badge && (
              <div className="absolute -top-3 left-4 px-3 py-1 bg-[#2F5E96] text-white text-xs font-bold rounded-full shadow-md">
                {item.badge}
              </div>
            )}
            
            <div className={`flex flex-col md:flex-row md:items-start md:justify-between gap-4 ${item.isPremium ? 'mt-2' : ''}`}>
              <div className="flex-1">
                <h3 className={`font-bold mb-1 ${item.isPremium ? 'text-[#2F5E96] text-lg' : 'text-[#27292E]'}`}>
                  {item.name}
                </h3>
                <p className="text-sm text-gray-500 mb-3">{item.description}</p>
                
                {/* Price per m² adjustment */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-gray-600">Price per m²:</span>
                  <button
                    onClick={() => onPriceChange(item.id, -5)}
                    className={`w-8 h-8 flex items-center justify-center bg-white border-2 rounded-lg transition-colors ${
                      item.isPremium 
                        ? 'border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/10' 
                        : 'border-[#300122] text-[#300122] hover:bg-[#300122]/10'
                    }`}
                    data-testid={`${item.id}-price-decrease`}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className={`font-bold text-lg min-w-[80px] text-center ${item.isPremium ? 'text-[#2F5E96]' : 'text-[#300122]'}`}>
                    £{item.pricePerM2}
                  </span>
                  <button
                    onClick={() => onPriceChange(item.id, 5)}
                    className={`w-8 h-8 flex items-center justify-center text-white rounded-lg transition-colors ${
                      item.isPremium 
                        ? 'bg-[#2F5E96] hover:bg-[#2F5E96]/90' 
                        : 'bg-[#300122] hover:bg-[#4a0636]'
                    }`}
                    data-testid={`${item.id}-price-increase`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {item.pricePerM2 !== item.basePrice && (
                    <span className="text-xs text-gray-400">
                      (base: £{item.basePrice})
                    </span>
                  )}
                </div>
              </div>

              {/* Square metres input */}
              <div className="md:w-48">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                  Square Metres
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={item.squareMetres || ''}
                  onChange={(e) => onSquareMetresChange(item.id, e.target.value)}
                  placeholder="0"
                  className={`h-12 text-lg border-2 rounded-xl text-center ${
                    item.isPremium ? 'border-[#2F5E96] focus:ring-[#2F5E96]' : ''
                  }`}
                  data-testid={`${item.id}-sqm-input`}
                />
              </div>
            </div>

            {/* Ox blend (optional) — lets "Get material price" pre-fill the calculator */}
            {item.squareMetres > 0 && onBlendChange && (
              <div className="mt-3">
                <Label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Palette className="w-3.5 h-3.5" /> Ox blend colour <span className="text-gray-400">(optional — used for your material price)</span></Label>
                <select
                  value={item.blend || ''}
                  onChange={(e) => onBlendChange(item.id, e.target.value)}
                  className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm"
                  data-testid={`${item.id}-blend-select`}
                >
                  <option value="">Not chosen yet</option>
                  {SURFACE_BLEND_GROUPS.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.blends.map((b) => <option key={b} value={b}>{b.replace(/\*/g, '')}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {/* Subtotal */}
            {item.squareMetres > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-gray-600">
                  {item.squareMetres} m² × £{item.pricePerM2}/m²
                </span>
                <span className={`font-bold text-xl ${item.isPremium ? 'text-[#2F5E96]' : 'text-[#300122]'}`}>
                  £{(item.squareMetres * item.pricePerM2).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Running Total */}
      {hasItems && (
        <div className="mt-6 p-4 bg-[#300122] text-white rounded-xl">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-white/80 text-sm">Surfacing Total</p>
              <p className="text-sm text-white/60">{totals.totalSquareMetres} m² total</p>
            </div>
            <p className="text-3xl font-bold">
              £{totals.surfacingTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-4 mt-8">
        <Button
          onClick={onBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={onContinue}
          disabled={!hasItems}
          className="flex-1 bg-[#300122] hover:bg-[#4a0636] text-white h-14 text-lg font-bold rounded-xl disabled:opacity-50"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default StepSurfacing;
